import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Calendar,
	Clock,
	MapPin,
	User,
	Phone,
	Mail,
	CreditCard,
	CheckCircle2,
	Search,
	Filter,
	ChevronLeft,
	ChevronRight,
	X as XIcon,
	Ban,
	RefreshCw,
	FileText,
} from "lucide-react";
import "../styles/admin-base.css";
import "../styles/client-bookings.css";
import * as api from "../api/admin";

const STATUS_OPTIONS = [
	{ value: "all", label: "All Statuses" },
	{ value: "pending", label: "Pending" },
	{ value: "approved", label: "Approved" },
	{ value: "rejected", label: "Rejected" },
	{ value: "cancelled", label: "Cancelled" },
	{ value: "completed", label: "Completed" },
];

const STATUS_CHIPS = {
	pending: { label: "Pending", className: "is-orange" },
	approved: { label: "Approved", className: "is-green" },
	rejected: { label: "Rejected", className: "is-red" },
	cancelled: { label: "Cancelled", className: "" },
	completed: { label: "Completed", className: "is-blue" },
};

const PAYMENT_CHIPS = {
	paid: { label: 'Paid', className: 'is-green' },
	refunded: { label: 'Refunded', className: 'is-green' },
	processing: { label: 'Processing', className: 'is-blue' },
	pending: { label: 'Pending', className: 'is-orange' },
	unpaid: { label: 'Unpaid', className: 'is-orange' },
	failed: { label: 'Failed', className: 'is-red' },
	expired: { label: 'Expired', className: '' },
	waived: { label: 'Waived', className: 'is-blue' },
};

const DEFAULT_ADMIN_ID = (() => {
	const envValue = process.env.REACT_APP_DEFAULT_ADMIN_ID;
	const parsed = envValue ? Number.parseInt(envValue, 10) : NaN;
	if (Number.isFinite(parsed) && parsed > 0) {
		return parsed;
	}
	return 1;
})();

function formatDate(value) {
	if (!value) return "—";
	const date = new Date(value);
	if (!Number.isNaN(date.getTime())) {
		return date.toLocaleDateString("en-PH", {
			month: "short",
			day: "2-digit",
			year: "numeric",
		});
	}
	return String(value);
}

function formatTime(value) {
	if (!value) return "—";
	// value is in HH:MM:SS format
	const match = String(value).match(/^(\d{2}):(\d{2})/);
	if (match) {
		const hours = parseInt(match[1], 10);
		const minutes = match[2];
		const period = hours >= 12 ? "PM" : "AM";
		const displayHours = hours % 12 || 12;
		return `${displayHours}:${minutes} ${period}`;
	}
	return String(value);
}

function formatPeso(value) {
	const num = Number(value);
	if (!Number.isFinite(num)) return "₱0.00";
	return `₱${num.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function resolvePaymentChip(status) {
	const normalized = (status || '').toString().trim().toLowerCase();
	return PAYMENT_CHIPS[normalized || 'unpaid'] || { label: normalized || 'Unpaid', className: 'is-orange' };
}

function resolveEffectivePaymentStatus(booking) {
	const status = (booking?.status || '').toString().trim().toLowerCase();
	const paymentStatus = (booking?.paymentStatus || '').toString().trim().toLowerCase();
	const refundStatus = (booking?.refundStatus || '').toString().trim().toLowerCase();

	if (status !== 'rejected') {
		return paymentStatus || 'unpaid';
	}

	if (refundStatus === 'succeeded') return 'refunded';
	if (refundStatus === 'failed' || refundStatus === 'canceled' || refundStatus === 'cancelled') return 'failed';
	if (refundStatus === 'processing' || refundStatus === 'pending') return 'processing';

	if (paymentStatus === 'refunded') return 'refunded';
	if (paymentStatus === 'failed') return 'failed';
	if (paymentStatus === 'processing') return 'processing';

	return 'processing';
}

function formatRefundStatus(value) {
	const normalized = (value || '').toString().trim();
	if (!normalized) return 'N/A';
	return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

const TABLE_PAGE_SIZE = 7;

export default function ClientBookings() {
	const [bookings, setBookings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState(null);
	const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 50 });
	const [analytics, setAnalytics] = useState(null);

	const [filters, setFilters] = useState({
		status: "all",
		venueId: "all",
		dateFrom: "",
		dateTo: "",
	});

	const [venues, setVenues] = useState([]);

	const [detailDialog, setDetailDialog] = useState({ isOpen: false, booking: null });
	const [actionState, setActionState] = useState({ processing: false, error: null });
	const [rejectDialog, setRejectDialog] = useState({ isOpen: false, reason: '' });
	const [waybillModal, setWaybillModal] = useState({ open: false, booking: null, html: '', loading: false, error: null });
	const waybillFrameRef = useRef(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);

	const activeFilterCount = [
		filters.status !== "all",
		filters.venueId !== "all",
		Boolean(filters.dateFrom),
		Boolean(filters.dateTo),
	].filter(Boolean).length;

	const fetchBookings = useCallback(async () => {
		setLoading(true);
		setErrorMessage(null);
		try {
			const params = { pageSize: 50 };

			if (filters.status !== "all") {
				params.status = filters.status;
			}
			if (filters.venueId !== "all") {
				params.venueId = filters.venueId;
			}
			if (filters.dateFrom) {
				params.dateFrom = filters.dateFrom;
			}
			if (filters.dateTo) {
				params.dateTo = filters.dateTo;
			}

			const res = await api.listVenueBookings(params);
			const items = res.data.results || [];
			setBookings(items);
			setMeta(res.data.meta || { total: 0, page: 1, pageSize: 50 });
		} catch (err) {
			console.error("Unable to load bookings", err);
			setErrorMessage("Unable to load bookings. Please try again.");
		} finally {
			setLoading(false);
		}
	}, [filters]);

	const fetchVenues = useCallback(async () => {
		try {
			const res = await api.listVenues({ pageSize: 100, isDeleted: false, isVisible: true });
			setVenues(res.data.results || []);
		} catch (err) {
			console.error("Unable to load venues", err);
		}
	}, []);

	const fetchAnalytics = useCallback(async () => {
		try {
			const res = await api.getVenueBookingAnalytics();
			setAnalytics(res.data || null);
		} catch (err) {
			console.error("Unable to load analytics", err);
		}
	}, []);

	useEffect(() => {
		fetchBookings();
	}, [fetchBookings]);

	useEffect(() => {
		fetchVenues();
		fetchAnalytics();
	}, [fetchVenues, fetchAnalytics]);

	const handleFilterChange = (field) => (event) => {
		setFilters((prev) => ({ ...prev, [field]: event.target.value }));
	};

	const openDetailDialog = async (booking) => {
		setDetailDialog({ isOpen: true, booking });
		setActionState({ processing: false, error: null });

		try {
			const res = await api.getVenueBooking(booking.id);
			const fullBooking = res?.data?.booking;
			if (fullBooking) {
				setDetailDialog({ isOpen: true, booking: fullBooking });
			}
		} catch (err) {
			console.error('Unable to load full booking details', err);
			setActionState((prev) => ({
				...prev,
				error: prev.error || 'Loaded partial booking details only. Some refund data may be unavailable.',
			}));
		}
	};

	const closeDetailDialog = () => {
		setDetailDialog({ isOpen: false, booking: null });
		setActionState({ processing: false, error: null });
	};

	const handleApprove = async () => {
		if (!detailDialog.booking) return;

		setActionState({ processing: true, error: null });

		try {
			const adminUser = localStorage.getItem("adminUser");
			const adminData = adminUser ? JSON.parse(adminUser) : {};

			await api.approveVenueBooking(detailDialog.booking.id, {
				adminId: adminData.id || DEFAULT_ADMIN_ID,
				adminName: `${adminData.firstName || ""} ${adminData.lastName || ""}`.trim() || "Admin",
			});

			await fetchBookings();
			await fetchAnalytics();
			closeDetailDialog();
		} catch (err) {
			console.error("Failed to approve booking", err);
			setActionState({
				processing: false,
				error: err.response?.data?.message || "Failed to approve booking. Please try again.",
			});
		}
	};

	const handleReject = () => {
		if (!detailDialog.booking) return;
		setRejectDialog({ isOpen: true, reason: '' });
	};

	const confirmReject = async () => {
		if (!detailDialog.booking) return;
		setActionState({ processing: true, error: null });

		try {
			const adminUser = localStorage.getItem("adminUser");
			const adminData = adminUser ? JSON.parse(adminUser) : {};

			await api.rejectVenueBooking(detailDialog.booking.id, {
				adminId: adminData.id || DEFAULT_ADMIN_ID,
				adminName: `${adminData.firstName || ""} ${adminData.lastName || ""}`.trim() || "Admin",
				reason: rejectDialog.reason.trim() || "No reason provided",
			});

			setRejectDialog({ isOpen: false, reason: '' });
			await fetchBookings();
			await fetchAnalytics();
			closeDetailDialog();
		} catch (err) {
			console.error("Failed to reject booking", err);
			setActionState({
				processing: false,
				error: err.response?.data?.message || "Failed to reject booking. Please try again.",
			});
		}
	};

	const renderStatusChip = (status) => {
		const config = STATUS_CHIPS[status] || { label: status, className: "" };
		return <span className={`admin-chip ${config.className}`}>{config.label}</span>;
	};

	const renderPaymentChip = (booking) => {
		const config = resolvePaymentChip(resolveEffectivePaymentStatus(booking));
		return <span className={`admin-chip ${config.className}`}>{config.label}</span>;
	};

	const handleRefresh = useCallback(async () => {
		await Promise.all([fetchBookings(), fetchVenues(), fetchAnalytics()]);
	}, [fetchBookings, fetchVenues, fetchAnalytics]);

	const filteredBookings = useMemo(() => {
		const normalizedQuery = searchQuery.trim().toLowerCase();
		if (!normalizedQuery) {
			return bookings;
		}

		return bookings.filter((booking) => {
			const searchableValues = [
				booking?.customerName,
				booking?.venueName,
				booking?.status,
				resolveEffectivePaymentStatus(booking),
				booking?.email,
				booking?.mobileNumber,
				booking?.membershipNo,
				booking?.id,
			];

			return searchableValues.some((value) =>
				String(value ?? "").toLowerCase().includes(normalizedQuery),
			);
		});
	}, [bookings, searchQuery]);

	const totalPages = Math.max(1, Math.ceil(filteredBookings.length / TABLE_PAGE_SIZE));

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	const paginatedBookings = useMemo(() => {
		const startIndex = (currentPage - 1) * TABLE_PAGE_SIZE;
		return filteredBookings.slice(startIndex, startIndex + TABLE_PAGE_SIZE);
	}, [currentPage, filteredBookings]);

	const handleSearchChange = (event) => {
		setSearchQuery(event.target.value);
		setCurrentPage(1);
	};

	const handlePreviousPage = () => {
		setCurrentPage((previous) => Math.max(1, previous - 1));
	};

	const handleNextPage = () => {
		setCurrentPage((previous) => Math.min(totalPages, previous + 1));
	};

	const handlePrintWaybill = async (booking) => {
		if (!booking?.id) return;
		setWaybillModal({ open: true, booking, html: '', loading: true, error: null });
		try {
			const response = await api.getVenueBookingWaybill(booking.id);
			const html = String(response?.data || '').trim();
			if (!html) {
				setWaybillModal((prev) => ({ ...prev, loading: false, error: 'Waybill HTML is empty.' }));
				return;
			}
			setWaybillModal((prev) => ({ ...prev, html, loading: false, error: null }));
		} catch (err) {
			setWaybillModal((prev) => ({
				...prev,
				loading: false,
				error: err?.response?.data?.message || 'Unable to load waybill preview.',
			}));
		}
	};

	const closeWaybillModal = () => {
		setWaybillModal({ open: false, booking: null, html: '', loading: false, error: null });
	};

	const printWaybillFromModal = () => {
		try {
			const frameWindow = waybillFrameRef.current?.contentWindow;
			if (!frameWindow) return;
			frameWindow.focus();
			frameWindow.print();
		} catch (err) {
			console.error('Waybill print failed', err);
		}
	};

	return (
		<div className="admin-page client-bookings-page">
			<div className="admin-stack-lg">
				<header className="admin-surface cb-hero">
					<div className="cb-hero__intro">
						<span className="cb-hero__eyebrow">Booking Operations</span>
						<h1>Client Bookings</h1>
						<p className="admin-muted">View and manage venue booking requests</p>

					</div>
					<div className="cb-hero__actions">
						<button
							type="button"
							className={`admin-icon-button admin-icon-button--refresh ${loading ? "is-loading" : ""}`}
							onClick={handleRefresh}
							disabled={loading}
							aria-label="Refresh bookings"
							title="Refresh bookings"
						>
							<RefreshCw size={18} />
						</button>
					</div>
				</header>

				{analytics && (
					<div className="admin-card-grid cols-4 cb-analytics-section cb-overview">
						<article className="admin-card">
							<div className="admin-card__label">Total Bookings</div>
							<div className="admin-row">
								<div className="admin-card__value">
									{analytics.statistics?.totalBookings || 0}
								</div>
								<span className="admin-chip is-blue"><Calendar size={18} /></span>
							</div>
							<div className="admin-card__meta">All booking requests</div>
						</article>
						<article className="admin-card">
							<div className="admin-card__label">Pending</div>
							<div className="admin-row">
								<div className="admin-card__value">
									{analytics.statistics?.pendingCount || 0}
								</div>
								<span className="admin-chip is-orange"><Clock size={18} /></span>
							</div>
							<div className="admin-card__meta">Awaiting review</div>
						</article>
						<article className="admin-card">
							<div className="admin-card__label">Approved</div>
							<div className="admin-row">
								<div className="admin-card__value">
									{analytics.statistics?.approvedCount || 0}
								</div>
								<span className="admin-chip is-green"><CheckCircle2 size={18} /></span>
							</div>
							<div className="admin-card__meta">Confirmed bookings</div>
						</article>
						<article className="admin-card">
							<div className="admin-card__label">Cancelled</div>
							<div className="admin-row">
								<div className="admin-card__value">
									{analytics.statistics?.cancelledCount || 0}
								</div>
								<span className="admin-chip is-red"><Ban size={18} /></span>
							</div>
							<div className="admin-card__meta">Cancelled bookings</div>
						</article>
					</div>
				)}

				<div className="admin-surface cb-filters-surface cb-toolbar-surface">
					<div className="admin-row members-table__search-row cb-search-row">
						<div className="cb-search-field">
							<span className="admin-card__label">Search Bookings</span>
							<div className="admin-card cb-search-card">
								<Search size={18} color="#64748b" />
								<input
									type="text"
									value={searchQuery}
									onChange={handleSearchChange}
									placeholder="Search by client, venue, status, payment, contact"
									className="cb-search-input"
								/>
							</div>
						</div>
						<div className="id-card-queue__filter-button-wrap cb-filter-toggle-wrap">
							<button
								type="button"
								className={`admin-button admin-button--ghost cb-filter-toggle ${showFilters ? "is-open" : ""} ${activeFilterCount > 0 ? "is-active" : ""}`}
								onClick={() => setShowFilters((open) => !open)}
								aria-pressed={showFilters}
							>
								<Filter size={18} /> Filters
								{activeFilterCount > 0 ? (
									<span className="cb-filter-toggle__badge">{activeFilterCount}</span>
								) : null}
							</button>
						</div>
					</div>

					{showFilters ? (
						<div className="admin-row admin-row--filters-inline cb-filters-inline">
						<div className="admin-field cb-filter-field">
							<label>Status</label>
							<select value={filters.status} onChange={handleFilterChange("status")}>
								{STATUS_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</select>
						</div>

						<div className="admin-field cb-filter-field">
							<label>Venue</label>
							<select value={filters.venueId} onChange={handleFilterChange("venueId")}>
								<option value="all">All Venues</option>
								{venues.map((venue) => (
									<option key={venue.id} value={venue.id}>
										{venue.name}
									</option>
								))}
							</select>
						</div>

						<div className="admin-field cb-filter-field--narrow">
							<label>From Date</label>
							<input
								type="date"
								value={filters.dateFrom}
								onChange={handleFilterChange("dateFrom")}
							/>
						</div>

						<div className="admin-field cb-filter-field--narrow">
							<label>To Date</label>
							<input
								type="date"
								value={filters.dateTo}
								onChange={handleFilterChange("dateTo")}
							/>
						</div>
						</div>
					) : null}
				</div>

				{errorMessage && (
					<div className="admin-surface cb-error-banner">
						{errorMessage}
					</div>
				)}

				{loading ? (
					<div className="admin-surface cb-centered-state">
						<p className="admin-muted">Loading bookings...</p>
					</div>
				) : bookings.length === 0 ? (
					<div className="admin-surface cb-centered-state">
						<p className="admin-muted">No bookings found matching the filters.</p>
					</div>
				) : filteredBookings.length === 0 ? (
					<div className="admin-surface cb-centered-state">
						<p className="admin-muted">No bookings match your search query.</p>
					</div>
				) : (
					<div className="admin-surface cb-table-surface">
						<div className="cb-table-wrapper admin-table-wrapper admin-scroll">
							<table className="admin-table cb-table">
								<colgroup>
									<col className="cb-col-id" />
									<col className="cb-col-customer" />
									<col className="cb-col-venue" />
									<col className="cb-col-date" />
									<col className="cb-col-time" />
									<col className="cb-col-contact" />
									<col className="cb-col-status" />
									<col className="cb-col-status" />
								</colgroup>
								<thead>
									<tr>
										<th>ID</th>
										<th>Customer</th>
										<th>Venue</th>
										<th>Date</th>
										<th>Time</th>
										<th>Contact</th>
										<th>Payment</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>
									{paginatedBookings.map((booking) => (
										<tr
											key={booking.id}
											className="cb-table-row"
											onClick={() => openDetailDialog(booking)}
										>
											<td>
												<span className="admin-pill">#{booking.id}</span>
											</td>
											<td>
												<strong>{booking.customerName}</strong>
												<p className="admin-muted cb-cell-sub">
													Member ID: {booking.membershipNo || "—"}
												</p>
											</td>
											<td>
												<MapPin size={14} className="cb-inline-icon" />
												{booking.venueName}
												<p className="admin-muted cb-cell-sub">
													{booking.venueType}
												</p>
											</td>
											<td>
												<Calendar size={14} className="cb-inline-icon" />
												{formatDate(booking.bookingDate)}
											</td>
											<td>
												<Clock size={14} className="cb-inline-icon" />
												{formatTime(booking.startTime)} - {formatTime(booking.endTime)}
											</td>
											<td>
												<p className="admin-muted cb-contact-line">
													<Phone size={12} className="cb-inline-icon-sm" /> {booking.mobileNumber || "—"}
												</p>
												<p className="admin-muted cb-contact-line">
													<Mail size={12} className="cb-inline-icon-sm" /> {booking.email || "—"}
												</p>
											</td>
											<td>{renderPaymentChip(booking)}</td>
											<td>{renderStatusChip(booking.status)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<footer className="admin-row cb-table__footer">
							<span className="admin-muted">Page {currentPage} of {totalPages}</span>
							<span className="admin-pill is-blue">Showing {paginatedBookings.length} of {filteredBookings.length} ({meta.total} total)</span>
							<div className="admin-actions">
								<button
									type="button"
									className="admin-button cb-pager-button"
									onClick={handlePreviousPage}
									disabled={currentPage <= 1}
								>
									<ChevronLeft size={16} /> Previous
								</button>
								<button
									type="button"
									className="admin-button cb-pager-button"
									onClick={handleNextPage}
									disabled={currentPage >= totalPages}
								>
									Next <ChevronRight size={16} />
								</button>
							</div>
						</footer>
					</div>
				)}
			</div>

			{detailDialog.isOpen && detailDialog.booking && (
				<div className="admin-dialog">
					<div className="admin-dialog__backdrop" onClick={closeDetailDialog} />
					<div className="admin-dialog__panel cb-dialog-panel">
						<div className="admin-dialog__header">
							<h2>Booking Details</h2>
							<button
								type="button"
								onClick={closeDetailDialog}
								className="admin-dialog__close"
							>
								<XIcon size={20} />
							</button>
						</div>

						<div className="admin-dialog__body">
							{actionState.error && (
								<div className="cb-dialog-error">
									{actionState.error}
								</div>
							)}

							<div className="cb-details-layout">
								<section className="cb-details-section cb-details-section--primary">
									<div className="cb-details-section__header">
										<Calendar size={16} className="cb-details-section__icon" />
										<h4 className="cb-details-section__title">Primary Meta</h4>
									</div>
									<div className="cb-details-grid cb-details-grid--meta">
										<div className="cb-details-item">
											<p className="cb-details-label">Booking ID</p>
											<p className="cb-details-value">#{detailDialog.booking.id}</p>
										</div>
										<div className="cb-details-item">
											<p className="cb-details-label">Status</p>
											<div className="cb-details-value">{renderStatusChip(detailDialog.booking.status)}</div>
										</div>
										<div className="cb-details-item">
											<p className="cb-details-label">Payment Status</p>
											<div className="cb-details-value">{renderPaymentChip(detailDialog.booking)}</div>
										</div>
									</div>
								</section>

							{detailDialog.booking.status === "rejected" && (
								<div className="cb-refund-summary">
									<h3 className="cb-section-title cb-refund-summary__title">
										<Ban size={18} className="cb-icon--danger" />
										Rejection &amp; Refund Summary
									</h3>
									<div className="cb-refund-summary__grid">
										<div className="cb-refund-summary__item cb-refund-summary__item--full">
											<p className="admin-muted cb-refund-summary__label">Rejection Reason</p>
											<p className="cb-refund-summary__value">
												{detailDialog.booking.rejectionReason || "No reason provided"}
											</p>
										</div>
										<div className="cb-refund-summary__item">
											<p className="admin-muted cb-refund-summary__label">Refund Status</p>
											<p className="cb-refund-summary__value">{formatRefundStatus(detailDialog.booking.refundStatus)}</p>
										</div>
										<div className="cb-refund-summary__item">
											<p className="admin-muted cb-refund-summary__label">Stripe Refund ID</p>
											<p className="cb-refund-summary__value">{detailDialog.booking.refundId || "N/A"}</p>
										</div>
										<div className="cb-refund-summary__item cb-refund-summary__item--full">
											<p className="admin-muted cb-refund-summary__label">Total Amount Refunded</p>
											<p className="cb-refund-summary__value cb-refund-summary__value--amount">
												{detailDialog.booking.refundAmountPhp != null ? formatPeso(detailDialog.booking.refundAmountPhp) : "N/A"}
											</p>
										</div>
									</div>
								</div>
							)}

								<section className="cb-details-section">
									<div className="cb-details-section__header">
										<User size={16} className="cb-details-section__icon" />
										<h4 className="cb-details-section__title">Customer Details</h4>
									</div>
									<div className="cb-details-grid">
										<div className="cb-details-item">
											<p className="cb-details-label">Customer Name</p>
											<p className="cb-details-value">{detailDialog.booking.customerName || "—"}</p>
										</div>
										<div className="cb-details-item">
											<p className="cb-details-label">Member ID</p>
											<p className="cb-details-value">{detailDialog.booking.membershipNo || "—"}</p>
										</div>
										<div className="cb-details-item">
											<p className="cb-details-label">Email</p>
											<p className="cb-details-value">{detailDialog.booking.email || "—"}</p>
										</div>
										<div className="cb-details-item">
											<p className="cb-details-label">Mobile Number</p>
											<p className="cb-details-value">{detailDialog.booking.mobileNumber || "—"}</p>
										</div>
									</div>
								</section>

								<section className="cb-details-section">
									<div className="cb-details-section__header">
										<MapPin size={16} className="cb-details-section__icon" />
										<h4 className="cb-details-section__title">Schedule &amp; Venue</h4>
									</div>
									<div className="cb-details-grid">
										<div className="cb-details-item">
											<p className="cb-details-label">Venue Name</p>
											<p className="cb-details-value">{detailDialog.booking.venueName || "—"}</p>
										</div>
										<div className="cb-details-item">
											<p className="cb-details-label">Venue Type</p>
											<p className="cb-details-value">{detailDialog.booking.venueType || "—"}</p>
										</div>
										<div className="cb-details-item">
											<p className="cb-details-label">Booking Date</p>
											<p className="cb-details-value">{formatDate(detailDialog.booking.bookingDate)}</p>
										</div>
										<div className="cb-details-item">
											<p className="cb-details-label">Time Range</p>
											<p className="cb-details-value">
												{formatTime(detailDialog.booking.startTime)} - {formatTime(detailDialog.booking.endTime)}
											</p>
										</div>
									</div>
								</section>

								<section className="cb-details-section">
									<div className="cb-details-section__header">
										<CreditCard size={16} className="cb-details-section__icon" />
										<h4 className="cb-details-section__title">Financials</h4>
									</div>
									<div className="cb-details-grid">
										<div className="cb-details-item">
											<p className="cb-details-label">Package</p>
											<p className="cb-details-value">{detailDialog.booking.pricingPackageLabel || '—'}</p>
										</div>
										<div className="cb-details-item">
											<p className="cb-details-label">Final Price</p>
											<p className="cb-details-value">{formatPeso(detailDialog.booking.paymentAmountPhp ?? detailDialog.booking.pricingFinalPricePhp)}</p>
										</div>
										{Number(detailDialog.booking.pricingDiscountPercentage || 0) > 0 && Number(detailDialog.booking.pricingOriginalPricePhp || 0) > 0 && (
											<div className="cb-details-item">
												<p className="cb-details-label">Original Price</p>
												<p className="cb-details-value">{formatPeso(detailDialog.booking.pricingOriginalPricePhp)}</p>
											</div>
										)}
										<div className="cb-details-item">
											<p className="cb-details-label">Paid At</p>
											<p className="cb-details-value">{formatDate(detailDialog.booking.paymentPaidAt)}</p>
										</div>
										<div className="cb-details-item">
											<p className="cb-details-label">Payment Window</p>
											<p className="cb-details-value">{detailDialog.booking.status === "pending" ? "Open" : "Closed"}</p>
										</div>
										<div className="cb-details-item cb-details-item--full">
											<p className="cb-details-label">Created</p>
											<p className="cb-details-value">{detailDialog.booking.timeAgo || formatDate(detailDialog.booking.createdAt)}</p>
										</div>
										{resolveEffectivePaymentStatus(detailDialog.booking) === 'paid' && (
											<div className="cb-details-item cb-details-item--full" style={{ paddingTop: '0.5rem' }}>
												<button
													type="button"
													className="admin-button is-primary cb-btn-flex"
													onClick={() => handlePrintWaybill(detailDialog.booking)}
												>
													<FileText size={16} />
													Print Waybill
												</button>
											</div>
										)}
									</div>
								</section>
							</div>
						</div>

						<div className="admin-dialog__footer">
							<button
								type="button"
								className="admin-button admin-button--ghost cb-btn-flex"
								onClick={closeDetailDialog}
							>
								<XIcon size={16} />
								Cancel
							</button>
							{detailDialog.booking.status === "pending" && (
								<>
									<button
										type="button"
										className="admin-button cb-btn-reject"
										onClick={handleReject}
										disabled={actionState.processing}
									>
										<XIcon size={16} />
										{actionState.processing ? "Processing..." : "Reject"}
									</button>
									<button
										type="button"
										className="admin-button is-primary cb-btn-approve"
										onClick={handleApprove}
										disabled={actionState.processing}
									>
										<CheckCircle2 size={18} />
										{actionState.processing ? "Processing..." : "Approve Booking"}
									</button>
								</>
							)}
						</div>
					</div>
				</div>
			)}

			{rejectDialog.isOpen && (
				<div className="admin-dialog" role="dialog" aria-modal="true" aria-label="Reject booking dialog">
					<div className="admin-dialog__backdrop" onClick={() => setRejectDialog({ isOpen: false, reason: '' })} />
					<div className="admin-dialog__panel" style={{ maxWidth: 460 }}>
						<div className="admin-dialog__header">
							<h2 className="admin-dialog__title">Reject Booking</h2>
						</div>
						<div className="admin-dialog__body">
							<p style={{ margin: 0, color: "#374151" }}>
								Are you sure you want to reject this booking? This action cannot be undone.
							</p>
							<div className="admin-field">
								<label htmlFor="bookingRejectReason">Rejection reason (optional):</label>
								<textarea
									id="bookingRejectReason"
									className="admin-input"
									rows={3}
									placeholder="Enter a reason for rejection..."
									value={rejectDialog.reason}
									onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
									style={{ resize: "vertical" }}
								/>
							</div>
						</div>
						<div className="admin-dialog__footer">
							<button
								type="button"
								className="admin-button admin-button--ghost"
								onClick={() => setRejectDialog({ isOpen: false, reason: '' })}
								disabled={actionState.processing}
							>
								Cancel
							</button>
							<button
								type="button"
								className="admin-button cb-btn-reject"
								onClick={confirmReject}
								disabled={actionState.processing}
							>
								{actionState.processing ? "Rejecting..." : "Reject Booking"}
							</button>
						</div>
					</div>
				</div>
			)}

			{waybillModal.open && (
				<div className="admin-dialog" role="dialog" aria-modal="true">
					<div className="admin-dialog__backdrop" onClick={closeWaybillModal} />
					<div className="admin-dialog__panel" style={{ maxWidth: 920 }}>
						<div className="admin-dialog__header">
							<h2>Waybill Preview {waybillModal.booking ? `- VB-${String(waybillModal.booking.id).padStart(6, '0')}` : ''}</h2>
							<button type="button" onClick={closeWaybillModal} className="admin-dialog__close">
								<XIcon size={20} />
							</button>
						</div>
						<div className="admin-dialog__body">
							{waybillModal.loading && (
								<p className="admin-muted" style={{ padding: '1rem 0' }}>Loading waybill preview...</p>
							)}
							{waybillModal.error && (
								<div className="cb-dialog-error">{waybillModal.error}</div>
							)}
							{!waybillModal.loading && !waybillModal.error && waybillModal.html && (
								<iframe
									ref={waybillFrameRef}
									title="Waybill Preview"
									srcDoc={waybillModal.html}
									style={{ width: '100%', minHeight: 600, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}
								/>
							)}
						</div>
						<div className="admin-dialog__footer">
							<button
								type="button"
								className="admin-button admin-button--ghost"
								onClick={closeWaybillModal}
							>
								Close
							</button>
							<button
								type="button"
								className="admin-button is-primary cb-btn-flex"
								onClick={printWaybillFromModal}
								disabled={waybillModal.loading || !waybillModal.html || Boolean(waybillModal.error)}
							>
								<FileText size={14} /> Print Waybill
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
